// ─────────────────────────────────────────────────────────────
//  SchengenChecker.jsx  — FIXED VERSION
//  Fixes: 1) Gender radio buttons now work
//         2) Radio button turns green on select
//         3) PDF receipt includes ALL user-entered information
// ─────────────────────────────────────────────────────────────

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import "./Schengen.css";
import PaymentForm, { PAYMENT_DETAILS_DEFAULT, validatePaymentDetails } from "../../../Common/PaymentForm/PaymentForm";
import {
  STEPS,
  CITIES,
  SLOT_RESULTS,
  APP_CENTERS,
  TRAVEL_PURPOSES,
  SPONSOR_OPTIONS,
  SPONSOR_ID_TYPES,
  PAYMENT_METHODS,
  FEE_BASE,
  FAQS,
  FAQ_KEYWORDS,
  DOCUMENT_PREVIEW,
  MONTH_NAMES,
  DOW_SHORT,
  COUNTRY_ALIASES,
  CONSENT_TERMS,
  PROGRESS_STAGES,
  saveTrackRecord,
  loadTrackRecord,
  generateTrackId,
  generateAppId,
  formatINR,
  buildCalendarCells,
  validateField,
  validateIdNumber,
  GENDER_OPTIONS,
  PRIOR_VISIT_OPTIONS,
  LOCALE,
  AOS_CONFIG,
} from "./Schengendata.js";

// ── useAOS Hook ───────────────────────────────────────────────
function useAOS() {
  useEffect(() => {
    if (window.AOS) {
      window.AOS.init(AOS_CONFIG);
    }
    return () => { if (window.AOS) window.AOS.refresh(); };
  }, []);
}

// ── Tracking Panel ────────────────────────────────────────────
function TrackingPanel({ initialId = "" }) {
  const [inputId,  setInputId]  = useState("");
  const [result,   setResult]   = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleLookup = (id) => {
    const lookupId = (id || inputId).trim().toUpperCase();
    if (!lookupId) return;
    const record = loadTrackRecord(lookupId);
    setSearched(true);
    if (record) { setResult(record); setNotFound(false); }
    else { setResult(null); setNotFound(true); }
  };

  const currentStageIdx = result ? PROGRESS_STAGES.findIndex(s => s.id === result.currentStage) : -1;

  return (
    <div className="sc__track-panel" data-aos="fade-up" data-aos-delay="100">
      <div className="sc__track-panel-header">
        <div className="sc__track-panel-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        </div>
        <div>
          <div className="sc__track-panel-title">Track Your Application</div>
          <div className="sc__track-panel-sub">Enter your Tracking ID to check real-time status</div>
        </div>
      </div>
      <div className="sc__track-input-row">
        <div className="sc__search-box" style={{ flex:1 }}>
          <span className="sc__search-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input className="sc__search-input" placeholder="e.g. TVC-ABC123-XY9Z" value={inputId}
            onChange={e => { setInputId(e.target.value.toUpperCase()); setSearched(false); setNotFound(false); setResult(null); }}
            onKeyDown={e => e.key === "Enter" && handleLookup()} />
        </div>
        <button className="sc__btn sc__btn--navy" onClick={() => handleLookup()} disabled={!inputId.trim()}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          Track
        </button>
      </div>
      {notFound && searched && (
        <div className="sc__track-not-found">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          No application found for <strong>{inputId}</strong>. Track id not found.
        </div>
      )}
      {result && (
        <div className="sc__track-result" style={{ animation:"slideUp 0.3s ease" }}>
          <div className="sc__track-result-header">
            <div>
              <div className="sc__track-result-id-label">Tracking ID</div>
              <div className="sc__track-result-id">{result.trackId}</div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div className="sc__track-result-id-label">Destination</div>
              <div className="sc__track-result-dest">{result.destination}</div>
            </div>
          </div>
          <div className="sc__track-applicants">
            {result.applications?.map((app, i) => (
              <div key={i} className="sc__track-applicant-chip">
                <span className="sc__app-summary-badge" style={{ width:20, height:20, fontSize:"0.65rem" }}>{i+1}</span>
                <span style={{ fontFamily:"var(--ff3)", fontSize:"0.78rem", fontWeight:600, color:"var(--navy)" }}>{app.name}</span>
                <span style={{ fontSize:"0.7rem", color:"var(--text-muted)" }}>{app.passportNo}</span>
              </div>
            ))}
          </div>
          <div className="sc__track-timeline">
            {PROGRESS_STAGES.map((stage, idx) => {
              const isDone   = idx <= currentStageIdx;
              const isActive = idx === currentStageIdx;
              const isFuture = idx > currentStageIdx;
              return (
                <div key={stage.id} className={`sc__track-stage${isDone?" sc__track-stage--done":""}${isActive?" sc__track-stage--active":""}${isFuture?" sc__track-stage--future":""}`}>
                  <div className="sc__track-stage-left">
                    <div className="sc__track-stage-dot">
                      {isDone && !isActive && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                      {isActive && <div className="sc__track-stage-pulse" />}
                      {isFuture && <div style={{ width:8, height:8, borderRadius:"50%", background:"var(--border)" }} />}
                    </div>
                    {idx < PROGRESS_STAGES.length - 1 && <div className={`sc__track-stage-line${isDone && idx < currentStageIdx?" sc__track-stage-line--done":""}`} />}
                  </div>
                  <div className="sc__track-stage-content">
                    <div className="sc__track-stage-label">{stage.icon} {stage.label}</div>
                    {(isDone || isActive) && <div className="sc__track-stage-desc">{stage.desc}</div>}
                    {isActive && result.updatedAt && <div className="sc__track-stage-time">Updated: {new Date(result.updatedAt).toLocaleString(LOCALE)}</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="sc__track-info-grid">
            <div className="sc__track-info-item"><div className="sc__track-info-label">Appointment Date</div><div className="sc__track-info-value">{result.appointmentDate}</div></div>
            <div className="sc__track-info-item"><div className="sc__track-info-label">Centre</div><div className="sc__track-info-value">{result.center}</div></div>
            <div className="sc__track-info-item"><div className="sc__track-info-label">Travellers</div><div className="sc__track-info-value">{result.applications?.length || 0}</div></div>
            <div className="sc__track-info-item"><div className="sc__track-info-label">Amount Paid</div><div className="sc__track-info-value">{formatINR(result.totalPaid || 0)}</div></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mini Calendar ─────────────────────────────────────────────
function MiniCalendar({ availableDates = [], selectedDate, onSelect, readOnly = false }) {
  const today = new Date();
  const [calYear,  setCalYear]  = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const cells = buildCalendarCells(calYear, calMonth);

  const prev = () => { if (calMonth === 0) { setCalMonth(11); setCalYear(y=>y-1); } else setCalMonth(m=>m-1); };
  const next = () => { if (calMonth === 11) { setCalMonth(0); setCalYear(y=>y+1); } else setCalMonth(m=>m+1); };

  return (
    <div className="sc__cal">
      <div className="sc__cal-header">
        {!readOnly && <button className="sc__cal-nav" onClick={prev}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg></button>}
        <div className="sc__cal-title">{MONTH_NAMES[calMonth]} {calYear}</div>
        {!readOnly && <button className="sc__cal-nav" onClick={next}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg></button>}
        {readOnly && <span />}
      </div>
      <div className="sc__cal-dow">{DOW_SHORT.map(d=><div key={d} className="sc__cal-dow-cell">{d}</div>)}</div>
      <div className="sc__cal-grid">
        {cells.map((c,i) => {
          const isAvail    = !c.out && availableDates.includes(c.num);
          const isToday    = !c.out && c.num===today.getDate() && calMonth===today.getMonth() && calYear===today.getFullYear();
          const isSelected = !c.out && selectedDate && selectedDate.day===c.num && selectedDate.month===calMonth && selectedDate.year===calYear;
          let cls = "sc__cal-day";
          if (c.out)             cls += " sc__cal-day--out";
          if (isAvail && !c.out) cls += " sc__cal-day--avail";
          if (isToday)           cls += " sc__cal-day--today";
          if (isSelected)        cls += " sc__cal-day--selected";
          return <div key={i} className={cls} onClick={() => isAvail && !readOnly && onSelect && onSelect({ day:c.num, month:calMonth, year:calYear })}>{c.num}</div>;
        })}
      </div>
    </div>
  );
}

// ── Step 1 — Search & Discovery ───────────────────────────────
function Step1Search({ onApply }) {
  useAOS();
  const [search,    setSearch]    = useState("");
  const [city,      setCity]      = useState("All Cities");
  const [noSlotDest,setNoSlotDest]= useState(null);
  const [faqSearch, setFaqSearch] = useState("");
  const [faqKw,     setFaqKw]     = useState("All Topics");
  const [openFaq,   setOpenFaq]   = useState(null);
  const [faqLimit,  setFaqLimit]  = useState(null);

  const getDefaultLimit = () => {
    if (typeof window==="undefined") return 8;
    if (window.innerWidth < 600) return 4;
    if (window.innerWidth < 900) return 6;
    return 8;
  };
  const defaultLimit = faqLimit ?? getDefaultLimit();

  const filtered = useMemo(() => {
    const raw = search.toLowerCase().trim();
    const aliasMatch = Object.entries(COUNTRY_ALIASES).find(([code])=>code.toLowerCase()===raw);
    const resolvedName = aliasMatch ? aliasMatch[1].toLowerCase() : raw;
    return SLOT_RESULTS.filter(r => {
      const matchSearch = resolvedName==="" || r.destination.toLowerCase().includes(resolvedName) || r.destination.toLowerCase().includes(raw);
      const matchCity = city==="All Cities" || r.city===city;
      return matchSearch && matchCity;
    });
  }, [search, city]);

  const filteredFaqs = useMemo(()=>FAQS.filter(f=>{
    const matchKw   = faqKw==="All Topics" || f.keyword===faqKw;
    const matchSrch = faqSearch==="" || f.question.toLowerCase().includes(faqSearch.toLowerCase()) || f.answer.toLowerCase().includes(faqSearch.toLowerCase());
    return matchKw && matchSrch;
  }), [faqKw, faqSearch]);

  const visibleFaqs = faqLimit===0 ? filteredFaqs : filteredFaqs.slice(0, defaultLimit);

  return (
    <>
      <div className="sc__search-row" data-aos="fade-up" data-aos-delay="50">
        <div className="sc__search-box">
          <span className="sc__search-icon"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
          <input className="sc__search-input" placeholder="Search by country or short code (e.g. FR, DE)…" value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        <select className="sc__select" value={city} onChange={e=>setCity(e.target.value)}>
          {CITIES.map(c=><option key={c}>{c}</option>)}
        </select>
      </div>
      {search.trim().length===2 && Object.keys(COUNTRY_ALIASES).includes(search.trim().toUpperCase()) && (
        <div className="sc__alias-hint">Showing results for <strong>{COUNTRY_ALIASES[search.trim().toUpperCase()]}</strong> ({search.trim().toUpperCase()})</div>
      )}
      <div className="sc__table-wrap" data-aos="fade-up" data-aos-delay="100">
        <table className="sc__table">
          <thead><tr><th>Destination</th><th>City</th><th>Dates</th><th>Availability</th><th>Action</th></tr></thead>
          <tbody>
            {filtered.length===0 ? (
              <tr><td colSpan={5} style={{textAlign:"center",padding:"2rem",color:"var(--text-muted)"}}>No results found.</td></tr>
            ) : filtered.map(r=>(
              <tr key={r.id}>
                <td><div className="sc__dest-cell"><div><div className="sc__dest-name">{r.destination}</div></div></div></td>
                <td data-label="City"><span className="sc__dest-city">{r.city}</span></td>
                <td data-label="Dates" style={{fontSize:"0.85rem",color:"var(--text-muted)"}}>{r.dates}</td>
                <td data-label="Availability">
                  {r.slots ? <span className="sc__badge sc__badge--available">Available</span> : <span className="sc__badge sc__badge--none">No Slots</span>}
                </td>
                <td data-label="Action">
                  {r.slots
                    ? <button className="sc__btn sc__btn--navy sc__btn--sm" onClick={()=>onApply(r)}>Apply</button>
                    : <button className="sc__btn sc__btn--ghost sc__btn--sm" onClick={()=>setNoSlotDest(r.destination)}>Info</button>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {noSlotDest && (
        <div className="sc__overlay-bg" onClick={()=>setNoSlotDest(null)}>
          <div className="sc__overlay" onClick={e=>e.stopPropagation()}>
            <div className="sc__overlay-title">No Slots Available</div>
            <p className="sc__overlay-text">There are currently <strong>no application centres</strong> with appointment availability for <strong>{noSlotDest}</strong> visa from India.<br/><br/>This can change daily. We recommend checking back tomorrow or enabling slot alerts.</p>
            <button className="sc__btn sc__btn--navy" onClick={()=>setNoSlotDest(null)}>Got it</button>
          </div>
        </div>
      )}
      <TrackingPanel />
      <div className="sc__faq" data-aos="fade-up" data-aos-delay="150">
        <div className="sc__section-title" style={{marginBottom:"0.35rem"}}>Frequently Asked Questions About Schengen Visa Appointments From India</div>
        <p className="sc__section-sub">Everything you need to know, answered clearly.</p>
        <div className="sc__faq-search-row">
          <div className="sc__search-box" style={{maxWidth:340}}>
            <span className="sc__search-icon"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
            <input className="sc__search-input" placeholder="Search questions…" value={faqSearch} onChange={e=>setFaqSearch(e.target.value)} />
          </div>
        </div>
        <div className="sc__faq-kw">
          {FAQ_KEYWORDS.map(k=><button key={k} className={`sc__kw-btn${faqKw===k?" sc__kw-btn--active":""}`} onClick={()=>{setFaqKw(k);setOpenFaq(null);}}>{k}</button>)}
        </div>
        <div className="sc__faq-grid">
          {visibleFaqs.map(f=>(
            <div key={f.id} className={`sc__faq-item${openFaq===f.id?" sc__faq-item--open":""}`}>
              <button className="sc__faq-q" onClick={()=>setOpenFaq(openFaq===f.id?null:f.id)}>
                <span>{f.question}</span>
                <span className="sc__faq-arrow"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg></span>
              </button>
              <div className="sc__faq-a">{f.answer}</div>
            </div>
          ))}
        </div>
        {filteredFaqs.length > defaultLimit && faqLimit !== 0 && (
          <div className="sc__faq-more"><button className="sc__btn sc__btn--outline" onClick={()=>setFaqLimit(0)}>View More ({filteredFaqs.length - visibleFaqs.length} more)</button></div>
        )}
        {faqLimit === 0 && filteredFaqs.length > getDefaultLimit() && (
          <div className="sc__faq-more"><button className="sc__btn sc__btn--ghost" onClick={()=>setFaqLimit(null)}>Show Less</button></div>
        )}
      </div>
    </>
  );
}

// ── Step 2 — Appointment Selection ────────────────────────────
function Step2Appointment({ slot, selectedDate, onDateSelect, selectedCenter, onCenterSelect }) {
  useAOS();
  const center = APP_CENTERS.find(c=>c.id===selectedCenter) || APP_CENTERS[0];
  const availIdx = selectedDate ? center.availableDates.indexOf(selectedDate.day) : -1;
  const prevDate = availIdx > 0 ? { day:center.availableDates[availIdx-1], month:new Date().getMonth(), year:new Date().getFullYear() } : null;
  const nextDate = availIdx < center.availableDates.length-1 ? { day:center.availableDates[availIdx+1], month:new Date().getMonth(), year:new Date().getFullYear() } : null;

  return (
    <>
      <div className="sc__section-title" data-aos="fade-up">Choose Application Centre</div>
      <p className="sc__section-sub" data-aos="fade-up" data-aos-delay="50">Select a centre and then pick your appointment date.</p>
      <div className="sc__centers" data-aos="fade-up" data-aos-delay="100">
        {APP_CENTERS.map(c=>(
          <div key={c.id} className={`sc__center-card${selectedCenter===c.id?" sc__center-card--active":""}`} onClick={()=>onCenterSelect(c.id)}>
            <div className="sc__center-radio"><div className="sc__center-radio-dot" /></div>
            <div className="sc__center-info"><div className="sc__center-name">{c.name}</div><div className="sc__center-addr">{c.address}</div></div>
            <div className="sc__center-slots">{c.slots} slots left</div>
          </div>
        ))}
      </div>
      <div className="sc__section-title" style={{fontSize:"1.1rem",marginBottom:"0.5rem"}}>Select Appointment Date</div>
      <p className="sc__section-sub" style={{marginBottom:"1rem"}}>
        <span style={{display:"inline-block",width:12,height:12,borderRadius:"50%",background:"var(--navy)",marginRight:6,verticalAlign:"middle"}}/>Navy = available &nbsp;&nbsp;
        <span style={{display:"inline-block",width:12,height:12,borderRadius:"50%",background:"var(--amber)",marginRight:6,verticalAlign:"middle"}}/>Orange = selected
      </p>
      <MiniCalendar availableDates={center.availableDates} selectedDate={selectedDate} onSelect={onDateSelect} />
      {selectedDate && (
        <div className="sc__appt-card" data-aos="fade-up" data-aos-delay="150">
          <div className="sc__appt-info">
            <div className="sc__appt-label">Selected Appointment</div>
            <div className="sc__appt-date">{selectedDate.day} {MONTH_NAMES[selectedDate.month]} {selectedDate.year}</div>
            <div className="sc__appt-loc">{center.name}</div>
          </div>
          <div className="sc__appt-nav">
            <button className={`sc__btn sc__btn--ghost sc__btn--sm${!prevDate?" sc__btn--disabled":""}`} onClick={()=>prevDate&&onDateSelect(prevDate)} disabled={!prevDate}>‹ Prev</button>
            <button className={`sc__btn sc__btn--ghost sc__btn--sm${!nextDate?" sc__btn--disabled":""}`} onClick={()=>nextDate&&onDateSelect(nextDate)} disabled={!nextDate}>Next ›</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Step 3 — Travel Info ──────────────────────────────────────
function Step3TravelInfo({ travelers, setTravelers, purpose, setPurpose }) {
  useAOS();
  return (
    <>
      <div className="sc__section-title" data-aos="fade-up">Travel Details</div>
      <p className="sc__section-sub" data-aos="fade-up" data-aos-delay="50">Tell us about your trip and travel purpose.</p>
      <div className="sc__count-row" data-aos="fade-up" data-aos-delay="100">
        <div className="sc__count-label">Number of Travellers</div>
        <div className="sc__count-ctrl">
          <button className="sc__count-btn" onClick={()=>setTravelers(t=>Math.max(1,t-1))}>−</button>
          <div className="sc__count-val">{travelers}</div>
          <button className="sc__count-btn" onClick={()=>setTravelers(t=>Math.min(9,t+1))}>+</button>
        </div>
      </div>
      <div className="sc__section-title" style={{fontSize:"1.05rem",marginBottom:"0.75rem"}}>Purpose of Travel</div>
      <div className="sc__purpose-grid" data-aos="fade-up" data-aos-delay="150">
        {TRAVEL_PURPOSES.map(p=>(
          <div key={p.id} className={`sc__purpose-card${purpose===p.id?" sc__purpose-card--active":""}`} onClick={()=>setPurpose(p.id)}>
            <div className="sc__purpose-name">{p.label}</div>
            <div className="sc__purpose-desc">{p.desc}</div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Validated Input ───────────────────────────────────────────
function ValidatedInput({ label, name, value, onChange, placeholder, type="text", maxLength, pattern, inputMode, required=true, hint, selectOptions }) {
  const [touched, setTouched] = useState(false);
  const error = touched ? validateField(name, value) : null;

  const handleChange = (e) => {
    let v = e.target.value;
    if (["firstName","lastName","placeOfIssue","emergName","emergRel","sponsorName","sponsorRel"].includes(name)) v = v.replace(/[0-9]/g,"");
    if (["phone","emergPhone","sponsorPhone"].includes(name)) v = v.replace(/[a-zA-Z]/g,"");
    if (name==="passportNo") v = v.toUpperCase().replace(/[^A-Z0-9]/g,"");
    onChange(name, v);
  };

  if (selectOptions) {
    return (
      <div className="sc__form-group">
        <label className="sc__label">{label}{required&&<span style={{color:"var(--danger)"}}> *</span>}</label>
        <select className={`sc__select-field${error?" sc__input--error":""}`} value={value||""} onChange={e=>{onChange(name,e.target.value);setTouched(true);}} onBlur={()=>setTouched(true)}>
          <option value="">Select…</option>
          {selectOptions.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {error && <div className="sc__field-error">{error}</div>}
        {hint && !error && <div className="sc__field-hint">{hint}</div>}
      </div>
    );
  }

  return (
    <div className="sc__form-group">
      <label className="sc__label">{label}{required&&<span style={{color:"var(--danger)"}}> *</span>}</label>
      <input className={`sc__input${error?" sc__input--error":""}`} type={type} placeholder={placeholder} value={value||""}
        onChange={handleChange} onBlur={()=>setTouched(true)} maxLength={maxLength} inputMode={inputMode} />
      {error && <div className="sc__field-error">{error}</div>}
      {hint && !error && <div className="sc__field-hint">{hint}</div>}
    </div>
  );
}

// ── File Upload Box ───────────────────────────────────────────
function UploadBox({ label, accept, value, onChange, sub }) {
  const fileData = typeof value==="object" ? value : null;
  const filename = fileData?.name || (typeof value==="string" ? value : null);

  const handleRemove = (e) => { e.preventDefault(); e.stopPropagation(); onChange(null); };
  const handleDownload = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (fileData?.data) { const a=document.createElement("a"); a.href=fileData.data; a.download=fileData.name||"document"; a.click(); }
  };
  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (ev) => onChange({ name:file.name, type:file.type, data:ev.target.result, size:file.size });
        reader.readAsDataURL(file);
      } else { onChange({ name:file.name, type:file.type, size:file.size }); }
    }
  };

  if (fileData) {
    return (
      <div className="sc__id-upload-preview">
        <div className="sc__id-upload-file">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--navy-light)" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <span className="sc__id-upload-filename">{fileData.name}</span>
          <div className="sc__id-upload-actions">
            <button className="sc__id-upload-btn" onClick={handleDownload} title="Download"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></button>
            <button className="sc__id-upload-btn sc__id-upload-btn--remove" onClick={handleRemove} title="Remove"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
          </div>
        </div>
        <div className="sc__id-upload-label">{label}</div>
      </div>
    );
  }

  return (
    <label className={`sc__upload-box${filename?" sc__upload-box--done":""}`}>
      <input type="file" accept={accept} style={{display:"none"}} onChange={handleChange} />
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{marginBottom:"0.5rem",color:filename?"var(--success)":"var(--navy-light)"}}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
      </svg>
      <div className="sc__upload-text">{label}</div>
      <div className="sc__upload-sub">{sub||"PDF, JPG or PNG · Max 5 MB · Click to browse"}</div>
      {filename && <div className="sc__upload-filename">✓ {filename}</div>}
    </label>
  );
}

// ── Sponsor ID Upload Box ─────────────────────────────────────
function SponsorIDUploadBox({ label, accept, fileData, onChange, sub }) {
  const handleRemove = (e) => { e.preventDefault(); e.stopPropagation(); onChange(null); };
  const handleDownload = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (fileData?.data) { const a=document.createElement("a"); a.href=fileData.data; a.download=fileData.name||"id-document"; a.click(); }
  };
  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (ev) => onChange({ name:file.name, type:file.type, data:ev.target.result, size:file.size });
        reader.readAsDataURL(file);
      } else { onChange({ name:file.name, type:file.type, size:file.size }); }
    }
  };

  if (fileData) {
    return (
      <div className="sc__id-upload-preview">
        <div className="sc__id-upload-file">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--navy-light)" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <span className="sc__id-upload-filename">{fileData.name}</span>
          <div className="sc__id-upload-actions">
            <button className="sc__id-upload-btn" onClick={handleDownload} title="Download"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></button>
            <button className="sc__id-upload-btn sc__id-upload-btn--remove" onClick={handleRemove} title="Remove"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></button>
          </div>
        </div>
        <div className="sc__id-upload-label">{label}</div>
      </div>
    );
  }

  return (
    <label className="sc__upload-box sc__upload-box--id">
      <input type="file" accept={accept} style={{display:"none"}} onChange={handleChange} />
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{marginBottom:"0.5rem",color:"var(--navy-light)"}}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
      </svg>
      <div className="sc__upload-text">{label}</div>
      <div className="sc__upload-sub">{sub||"JPG, PNG or PDF · Max 5 MB · Click to browse"}</div>
    </label>
  );
}

// ── FIX 1 & 2: Single Traveller Form — Gender buttons now work + turn green ──
function TravellerForm({ idx, data, onChange, totalTravellers }) {
  const upd = (k, v) => onChange(idx, k, v);
  const label = totalTravellers > 1 ? `Traveller ${idx + 1}` : "Primary Traveller";

  // FIX: Use div-based custom radio buttons instead of native <input type="radio"> inside <label>
  // Native radio buttons inside labels can cause event conflicts in React's synthetic event system.
  // Custom click handlers on divs bypass this issue entirely.
  const handleGenderSelect = (gender) => {
    upd("gender", gender);
  };

  return (
    <div className="sc__traveller-block" data-aos="fade-up" data-aos-delay={150 + (idx * 100)}>
      <div className="sc__traveller-heading">
        <span className="sc__traveller-heading-badge">{idx + 1}</span>
        {label}
        {data._appId && <span style={{ marginLeft:"auto", fontFamily:"var(--ff3)", fontSize:"0.7rem", fontWeight:600, color:"var(--text-muted)", background:"var(--navy-pale)", padding:"0.2rem 0.6rem", borderRadius:"20px" }}>ID: {data._appId}</span>}
      </div>

      <div className="sc__form-section">
        <div className="sc__form-section-title">Passport Upload</div>
        <div className="sc__form-grid">
          <div className="sc__form-group">
            <label className="sc__label">Passport Front Page <span style={{color:"var(--danger)"}}>*</span></label>
            <SponsorIDUploadBox label="Front Page" accept=".pdf,.jpg,.jpeg,.png" fileData={data.passportFront} onChange={v=>upd("passportFront",v)} sub="JPG, PNG or PDF · Max 5 MB" />
          </div>
          <div className="sc__form-group">
            <label className="sc__label">Passport Back Page <span style={{color:"var(--danger)"}}>*</span></label>
            <SponsorIDUploadBox label="Back Page" accept=".pdf,.jpg,.jpeg,.png" fileData={data.passportBack} onChange={v=>upd("passportBack",v)} sub="JPG, PNG or PDF · Max 5 MB" />
          </div>
        </div>
      </div>

      <div className="sc__form-section">
        <div className="sc__form-section-title">Passport Size Photo</div>
        <UploadBox label="Upload Passport Size Photo" accept=".jpg,.jpeg,.png" value={data.photoFile} onChange={v=>upd("photoFile",v)} sub="JPG or PNG · 35×45 mm · Max 2 MB · Click to browse" />
      </div>

      <div className="sc__form-section">
        <div className="sc__form-section-title">Personal Details</div>
        <div className="sc__form-grid">
          <ValidatedInput label="First Name" name="firstName" value={data.firstName} onChange={upd} placeholder="As on passport" hint="Letters only — no numbers" />
          <ValidatedInput label="Last Name" name="lastName" value={data.lastName} onChange={upd} placeholder="As on passport" hint="Letters only — no numbers" />

          {/* FIX 1 & 2: Completely custom gender selector — no native radio inputs */}
          <div className="sc__form-group">
            <label className="sc__label">Gender <span style={{color:"var(--danger)"}}>*</span></label>
            <div className="sc__gender-row">
              {GENDER_OPTIONS.map(g => (
                <div
                  key={g}
                  className={`sc__gender-opt${data.gender === g ? " sc__gender-opt--selected" : ""}`}
                  onClick={() => handleGenderSelect(g)}
                  role="radio"
                  aria-checked={data.gender === g}
                  tabIndex={0}
                  onKeyDown={e => (e.key === "Enter" || e.key === " ") && handleGenderSelect(g)}
                >
                  {/* Custom circle indicator — green when selected */}
                  <span className={`sc__gender-circle${data.gender === g ? " sc__gender-circle--selected" : ""}`}>
                    {data.gender === g && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </span>
                  <span className="sc__gender-label">{g}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="sc__form-group">
            <label className="sc__label">Date of Birth <span style={{color:"var(--danger)"}}>*</span></label>
            <input type="date" className="sc__input" value={data.dob||""} onChange={e=>upd("dob",e.target.value)} max={new Date().toISOString().split("T")[0]} />
          </div>
          <ValidatedInput label="Passport Number" name="passportNo" value={data.passportNo} onChange={upd} placeholder="e.g. A1234567" hint="1 letter + 7 digits" maxLength={8} />
          <ValidatedInput label="Place of Issue" name="placeOfIssue" value={data.placeOfIssue} onChange={upd} placeholder="e.g. Delhi" hint="Letters only" />
          <div className="sc__form-group">
            <label className="sc__label">Passport Valid Until <span style={{color:"var(--danger)"}}>*</span></label>
            <input type="date" className="sc__input" value={data.passportValidity||""} onChange={e=>upd("passportValidity",e.target.value)} min={new Date().toISOString().split("T")[0]} />
          </div>
          <div className="sc__form-group">
            <label className="sc__label">Schengen Visits (Last 5 Yrs)</label>
            <select className="sc__select-field" value={data.priorVisits||"0"} onChange={e=>upd("priorVisits",e.target.value)}>
              {PRIOR_VISIT_OPTIONS.map(v=><option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="sc__form-section">
        <div className="sc__form-section-title">Contact Details</div>
        <div className="sc__form-grid">
          <ValidatedInput label="Email Address" name="email" type="email" value={data.email} onChange={upd} placeholder="you@example.com" />
          <ValidatedInput label="Phone Number" name="phone" type="tel" value={data.phone} onChange={upd} placeholder="+91 98XXXXXXXX" hint="Numbers only" inputMode="tel" maxLength={15} />
        </div>
      </div>

      <div className="sc__form-section">
        <div className="sc__form-section-title">Emergency Contact</div>
        <div className="sc__form-grid">
          <ValidatedInput label="Contact Name" name="emergName" value={data.emergName} onChange={upd} placeholder="Full name" hint="Letters only" />
          <ValidatedInput label="Relationship" name="emergRel" value={data.emergRel} onChange={upd} placeholder="e.g. Spouse, Parent" hint="Letters only" />
          <div className="sc__form-group sc__form-group--full">
            <ValidatedInput label="Contact Phone" name="emergPhone" type="tel" value={data.emergPhone} onChange={upd} placeholder="+91 98XXXXXXXX" hint="Numbers only" inputMode="tel" maxLength={15} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Step 4 — Traveller Details ────────────────────────────────
function Step4TravellerDetails({ forms, setForms, travelers }) {
  useAOS();
  const onChange = (idx, k, v) => {
    setForms(prev => prev.map((f, fi) => fi===idx ? {...f, [k]:v} : f));
  };
  return (
    <>
      <div className="sc__section-title" data-aos="fade-up">Traveller Information</div>
      <p className="sc__section-sub" data-aos="fade-up" data-aos-delay="50">Fill in accurate details for all {travelers} traveller{travelers>1?"s":""} as per their passports.</p>
      <div className="sc__validation-note" data-aos="fade-up" data-aos-delay="100">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        Name fields accept letters only. Phone and ID fields accept numbers only. Fields are validated before proceeding.
      </div>
      {Array.from({ length: travelers }, (_,i) => (
        <TravellerForm key={i} idx={i} data={forms[i]||{}} onChange={onChange} totalTravellers={travelers} />
      ))}
    </>
  );
}

// ── Step 5 — Sponsor ──────────────────────────────────────────
function Step5Sponsor({ sponsor, setSponsor, sponsorData, setSponsorData, sponsorLetterUploaded, setSponsorLetterUploaded }) {
  useAOS();
  const [idErrors, setIdErrors] = useState({});
  const upd = (k, v) => setSponsorData(prev => ({...prev, [k]:v}));
  const idType = sponsorData.idType || "";

  const handleIdNumberChange = (v) => {
    upd("idNumber", v);
    if (idType) { const err=validateIdNumber(idType,v); setIdErrors(prev=>({...prev,idNumber:err})); }
  };

  return (
    <>
      <div className="sc__section-title" data-aos="fade-up">Sponsorship Details</div>
      <p className="sc__section-sub" data-aos="fade-up" data-aos-delay="50">Who is funding this trip?</p>
      <div className="sc__sponsor-grid" data-aos="fade-up" data-aos-delay="100">
        {SPONSOR_OPTIONS.map(s=>(
          <div key={s.id} className={`sc__sponsor-card${sponsor===s.id?" sc__sponsor-card--active":""}`} onClick={()=>setSponsor(s.id)}>
            <div className="sc__sponsor-name">{s.label}</div>
            <div className="sc__sponsor-desc">{s.desc}</div>
          </div>
        ))}
      </div>

      {sponsor==="other" && (
        <div style={{animation:"slideUp 0.25s ease"}}>
          <div className="sc__form-section">
            <div className="sc__form-section-title">Sponsor Personal Information</div>
            <div className="sc__form-grid">
              <ValidatedInput label="Sponsor Full Name" name="sponsorName" value={sponsorData.name} onChange={(k,v)=>upd("name",v)} placeholder="As per ID" hint="Letters only — no numbers" />
              <ValidatedInput label="Relationship" name="sponsorRel" value={sponsorData.rel} onChange={(k,v)=>upd("rel",v)} placeholder="e.g. Father, Employer" hint="Letters only" />
              <ValidatedInput label="Phone Number" name="sponsorPhone" type="tel" value={sponsorData.phone} onChange={(k,v)=>upd("phone",v)} placeholder="+91 98XXXXXXXX" inputMode="tel" maxLength={15} hint="Numbers only" />
              <ValidatedInput label="Email Address" name="sponsorEmail" type="email" value={sponsorData.email} onChange={(k,v)=>upd("email",v)} placeholder="sponsor@example.com" />
            </div>
          </div>
          <div className="sc__form-section">
            <div className="sc__form-section-title">Sponsor ID Details</div>
            <div className="sc__form-grid">
              <div className="sc__form-group">
                <label className="sc__label">ID Type <span style={{color:"var(--danger)"}}>*</span></label>
                <select className="sc__select-field" value={idType} onChange={e=>{upd("idType",e.target.value);upd("idNumber","");}}>
                  <option value="">Select ID type…</option>
                  {SPONSOR_ID_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div className="sc__form-group">
                <label className="sc__label">ID Number <span style={{color:"var(--danger)"}}>*</span>
                  {idType==="aadhaar" && <span className="sc__field-hint-inline"> (12 digits)</span>}
                  {idType==="pan"     && <span className="sc__field-hint-inline"> (AAAAA9999A)</span>}
                  {idType==="passport"&& <span className="sc__field-hint-inline"> (A1234567)</span>}
                </label>
                <input className={`sc__input${idErrors.idNumber?" sc__input--error":""}`}
                  placeholder={idType==="aadhaar"?"12-digit Aadhaar number":idType==="pan"?"e.g. ABCDE1234F":idType==="passport"?"e.g. A1234567":"Enter ID number"}
                  value={sponsorData.idNumber||""} maxLength={idType==="aadhaar"?12:idType==="pan"?10:idType==="passport"?8:20}
                  onChange={e=>{ let v=e.target.value; if(idType==="aadhaar")v=v.replace(/[^0-9]/g,""); if(idType==="pan")v=v.toUpperCase().replace(/[^A-Z0-9]/g,""); if(idType==="passport")v=v.toUpperCase().replace(/[^A-Z0-9]/g,""); handleIdNumberChange(v); }} />
                {idErrors.idNumber && <div className="sc__field-error">{idErrors.idNumber}</div>}
              </div>
            </div>
            {idType && (
              <div style={{marginTop:"1rem"}}>
                {idType==="passport" ? (
                  <div className="sc__form-grid">
                    <div className="sc__form-group"><label className="sc__label">Passport Front Page <span style={{color:"var(--danger)"}}>*</span></label><SponsorIDUploadBox label="Upload Front Page" accept=".jpg,.jpeg,.png,.pdf" fileData={sponsorData.idFront} onChange={v=>upd("idFront",v)} sub="JPG, PNG or PDF · Max 5 MB" /></div>
                    <div className="sc__form-group"><label className="sc__label">Passport Back Page <span style={{color:"var(--danger)"}}>*</span></label><SponsorIDUploadBox label="Upload Back Page" accept=".jpg,.jpeg,.png,.pdf" fileData={sponsorData.idBack} onChange={v=>upd("idBack",v)} sub="JPG, PNG or PDF · Max 5 MB" /></div>
                  </div>
                ) : (
                  <div className="sc__form-group"><label className="sc__label">ID Photo <span style={{color:"var(--danger)"}}>*</span></label><SponsorIDUploadBox label={`Upload ${SPONSOR_ID_TYPES.find(t=>t.id===idType)?.label} Photo`} accept=".jpg,.jpeg,.png,.pdf" fileData={sponsorData.idPhoto} onChange={v=>upd("idPhoto",v)} sub="JPG, PNG or PDF · Max 5 MB" /></div>
                )}
              </div>
            )}
          </div>
          <div className="sc__form-section">
            <div className="sc__form-section-title">Sponsorship Letter</div>
            <p style={{fontSize:"0.85rem",color:"var(--text-muted)",marginBottom:"1rem",lineHeight:1.6}}>A sponsorship letter is required. You can either upload your own or request our assistance.</p>
            <div style={{display:"flex",gap:"0.75rem",flexWrap:"wrap",marginBottom:"1rem"}}>
              <button className={`sc__btn ${sponsorData.letterAction==="upload"?"sc__btn--navy":"sc__btn--outline"}`} onClick={()=>upd("letterAction","upload")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Upload My Own Letter
              </button>
              <button className={`sc__btn ${sponsorData.letterAction==="service"?"sc__btn--green":"sc__btn--ghost"}`} onClick={()=>{upd("letterAction","service");setSponsorLetterUploaded(false);}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                Request Our Assistance
              </button>
            </div>
            {sponsorData.letterAction==="upload" && (
              <div style={{animation:"slideUp 0.2s ease"}}>
                <UploadBox label="Upload Sponsorship Letter" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" value={sponsorData.letterFile} onChange={v=>{upd("letterFile",v);setSponsorLetterUploaded(!!v);}} sub="PDF, DOC or Image · Max 10 MB" />
                {sponsorData.letterFile && <div style={{marginTop:"0.6rem",padding:"0.65rem 1rem",background:"var(--success-bg)",border:"1px solid var(--success)",borderRadius:"var(--radius-sm)",fontSize:"0.82rem",color:"var(--success)",display:"flex",alignItems:"center",gap:"0.5rem"}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>Sponsorship letter uploaded.</div>}
              </div>
            )}
            {sponsorData.letterAction==="service" && (
              <div style={{animation:"slideUp 0.2s ease",padding:"1rem",background:"var(--amber-bg)",border:"1px solid var(--amber)",borderRadius:"var(--radius-sm)",fontSize:"0.85rem",color:"var(--amber)",lineHeight:1.6}}>
                <strong>Assistance requested.</strong> Our team will prepare the sponsorship letter on your behalf.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── Step 6 — Documents ────────────────────────────────────────
function Step6Documents({ form, slot, purpose, sponsorLetterUploaded }) {
  useAOS();
  const name = `${form.firstName||"Applicant"} ${form.lastName||""}`.trim();
  const purposeLabel = TRAVEL_PURPOSES.find(p=>p.id===purpose)?.label||"Tourism";
  const docs = DOCUMENT_PREVIEW.filter(d => !(d.isSponsor && sponsorLetterUploaded));

  return (
    <>
      <div className="sc__section-title" data-aos="fade-up">Document Preparation</div>
      <p className="sc__section-sub" data-aos="fade-up" data-aos-delay="50">Your documents are being prepared. Finalised after payment.</p>
      {sponsorLetterUploaded && (
        <div style={{marginBottom:"1rem",padding:"0.65rem 1rem",background:"var(--success-bg)",border:"1px solid var(--success)",borderRadius:"var(--radius-sm)",fontSize:"0.82rem",color:"var(--success)",display:"flex",alignItems:"center",gap:"0.5rem"}} data-aos="fade-up">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          Sponsorship letter already uploaded — removed from checklist.
        </div>
      )}
      <div className="sc__doc-progress" data-aos="fade-up" data-aos-delay="150">
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:"0.5rem"}}>
          <span style={{fontFamily:"var(--ff3)",fontSize:"0.8rem",fontWeight:700,color:"var(--navy)"}}>Preparation Progress</span>
          <span style={{fontFamily:"var(--ff3)",fontSize:"0.8rem",color:"var(--text-muted)"}}>20%</span>
        </div>
        <div className="sc__doc-progress-bar-wrap"><div className="sc__doc-progress-bar" /></div>
        <div className="sc__doc-list">
          {docs.map(d=>(
            <div key={d.id} className="sc__doc-row">
              <span className="sc__doc-icon">{d.icon}</span>
              <span className="sc__doc-name">{d.label}</span>
              <span className={`sc__doc-status sc__doc-status--${d.status}`}>{d.status==="generating"?"Generating...":"Pending"}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="sc__section-title" style={{fontSize:"1.05rem",marginBottom:"0.75rem"}} data-aos="fade-up">Preview — Cover Letter</div>
      <div className="sc__preview-box" data-aos="fade-up">
        <strong>Date:</strong> {new Date().toLocaleDateString(LOCALE,{day:"2-digit",month:"long",year:"numeric"})}<br/><br/>
        To,<br/>The Visa Officer,<br/>{slot?.destination} Consulate, India<br/><br/>
        <strong>Subject: Application for Short-Stay Schengen Visa — {purposeLabel}</strong><br/><br/>
        Respected Sir/Madam,<br/><br/>
        I, <strong>{name}</strong>, holder of Passport No. <strong>{form.passportNo||"XXXXXXXXX"}</strong>, hereby apply for a short-stay Schengen visa to visit <strong>{slot?.destination}</strong> for the purpose of <strong>{purposeLabel}</strong>.<br/><br/>
        <em style={{color:"var(--amber)",fontFamily:"var(--ff3)",fontSize:"0.8rem"}}>This is a preview. Your finalised letter will be available for download after payment.</em>
      </div>
    </>
  );
}

// ── Step 7 — Agreement ────────────────────────────────────────
function Step7Agreement({ agreed, setAgreed, consentChecked, setConsentChecked }) {
  useAOS();
  return (
    <>
      <div className="sc__section-title" data-aos="fade-up">User Consent &amp; Agreement</div>
      <p className="sc__section-sub" data-aos="fade-up" data-aos-delay="50">Please read and acknowledge all terms before proceeding to payment.</p>
      <div className="sc__agreement-notice" data-aos="fade-up">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        We are an independent service provider and not affiliated with any embassy.
      </div>
      <div className="sc__terms-list" data-aos="fade-up" data-aos-delay="150">
        {CONSENT_TERMS.map(t=>(
          <div key={t.id} className="sc__term-item">
            <div className="sc__term-header"><div className="sc__term-title">{t.title}</div></div>
            <div className="sc__term-body">{t.body}</div>
          </div>
        ))}
      </div>
      <div className="sc__consent-final" data-aos="fade-up" data-aos-delay="200">
        <label className="sc__consent-label" onClick={()=>setConsentChecked(c=>!c)}>
          <div className={`sc__term-checkbox${consentChecked?" sc__term-checkbox--checked":""}`} style={{flexShrink:0}}>
            {consentChecked && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
          </div>
          <span>I confirm that all details provided are accurate. I agree to the Terms &amp; Conditions, consent to document processing, and accept the strict <strong>no-refund policy</strong>.</span>
        </label>
      </div>
      {agreed && (
        <div style={{padding:"1rem",background:"var(--success-bg)",border:"1.5px solid var(--success)",borderRadius:"var(--radius)",display:"flex",alignItems:"center",gap:"0.75rem"}}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          <div><div style={{fontFamily:"var(--ff3)",fontWeight:700,color:"var(--success)",fontSize:"0.9rem"}}>Agreement Confirmed</div><div style={{fontSize:"0.8rem",color:"var(--success)",marginTop:"0.15rem"}}>You may now proceed to payment.</div></div>
        </div>
      )}
    </>
  );
}

// ── Step 8 — Payment ──────────────────────────────────────────
function Step8Payment({ payMethod, setPayMethod, paymentDetails, setPayF, setPaymentDetails, travelers, onPay }) {
  useAOS();
  const method = PAYMENT_METHODS.find(m=>m.id===payMethod)||PAYMENT_METHODS[0];
  const sub   = (FEE_BASE.appointment + FEE_BASE.service) * travelers;
  const gst   = Math.round(sub * FEE_BASE.gstRate);
  const total = sub + gst + method.convenience;

  function handlePayClick() {
    const err = validatePaymentDetails(payMethod, paymentDetails);
    if (err) { alert(err); return; }
    onPay();
  }

  return (
    <div className="sc__payment-layout">
      <div>
        <div className="sc__section-title" data-aos="fade-up">Complete Payment</div>
        <p className="sc__section-sub" data-aos="fade-up" data-aos-delay="50">Choose your preferred payment method and fill in details.</p>
        {travelers > 1 && (
          <div style={{marginBottom:"1rem",padding:"0.65rem 1rem",background:"var(--navy-pale)",borderRadius:"var(--radius-sm)",fontSize:"0.85rem",color:"var(--navy-mid)",display:"flex",alignItems:"center",gap:"0.5rem"}} data-aos="fade-up">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {travelers} travellers × {formatINR(FEE_BASE.appointment + FEE_BASE.service)} = {formatINR((FEE_BASE.appointment + FEE_BASE.service) * travelers)} (before GST)
          </div>
        )}
        <div data-aos="fade-up" data-aos-delay="150">
          <PaymentForm selectedMethod={payMethod} onMethodSelect={(id)=>{setPayMethod(id);setPaymentDetails(PAYMENT_DETAILS_DEFAULT);}} details={paymentDetails} onDetailChange={setPayF} />
        </div>
      </div>
      <div>
        <div className="sc__fee-card" data-aos="fade-up" data-aos-delay="200">
          <div className="sc__fee-title">Fee Breakdown</div>
          <div className="sc__fee-rows">
            <div className="sc__fee-row"><span>Appointment Fee × {travelers}</span><strong>{formatINR(FEE_BASE.appointment * travelers)}</strong></div>
            <div className="sc__fee-row"><span>Service Fee × {travelers}</span><strong>{formatINR(FEE_BASE.service * travelers)}</strong></div>
            <div className="sc__fee-row"><span>GST (18%)</span><strong>{formatINR(gst)}</strong></div>
            {method.convenience > 0 && <div className="sc__fee-row"><span>Convenience Fee ({method.label})</span><strong>{formatINR(method.convenience)}</strong></div>}
          </div>
          <hr className="sc__fee-divider" />
          <div className="sc__fee-total">
            <div className="sc__fee-total-label">Total Payable</div>
            <div className="sc__fee-total-amt">{formatINR(total)}</div>
          </div>
          <div className="sc__pay-btn-wrap">
            <button className="sc__btn sc__btn--navy sc__btn--lg" style={{width:"100%"}} onClick={handlePayClick}>Pay {formatINR(total)}</button>
          </div>
          {/* <div className="sc__pay-secure">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            256-bit SSL encrypted · PCI-DSS compliant
          </div> */}
        </div>
      </div>
    </div>
  );
}

// ── App Detail Modal ──────────────────────────────────────────
function AppDetailModal({ app, onClose }) {
  return (
    <div className="sc__overlay-bg" onClick={onClose}>
      <div className="sc__overlay" style={{maxWidth:520,textAlign:"left"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1rem"}}>
          <div className="sc__overlay-title" style={{margin:0}}>Application Details</div>
          <button className="sc__btn sc__btn--ghost sc__btn--sm" onClick={onClose}>✕ Close</button>
        </div>
        <div style={{background:"var(--navy-pale)",borderRadius:"var(--radius-sm)",padding:"0.65rem 1rem",marginBottom:"1rem"}}>
          <div style={{fontFamily:"var(--ff3)",fontSize:"0.7rem",fontWeight:700,color:"var(--navy-light)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:"0.25rem"}}>Application ID</div>
          <div style={{fontFamily:"var(--ff3)",fontSize:"1rem",fontWeight:700,color:"var(--navy)"}}>{app.appId}</div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:"0.4rem"}}>
          {[["Traveller",app.name],["Passport No.",app.passportNo],["Gender",app.gender],["Date of Birth",app.dob],["Email",app.email],["Phone",app.phone],["Emergency Contact",app.emergName],["Destination",app.destination],["Appointment Date",app.date],["Centre",app.center]].map(([k,v])=>(
            <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"0.4rem 0",borderBottom:"1px solid var(--border)",fontSize:"0.87rem"}}>
              <span style={{color:"var(--text-muted)"}}>{k}</span>
              <span style={{fontFamily:"var(--ff3)",fontWeight:700,color:"var(--navy)",textAlign:"right",maxWidth:"60%"}}>{v||"—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Tracking ID Card ──────────────────────────────────────────
function TrackingIdCard({ trackId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(trackId).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2000);}).catch(()=>{
      const el=document.createElement("textarea"); el.value=trackId; document.body.appendChild(el); el.select(); document.execCommand("copy"); document.body.removeChild(el);
      setCopied(true); setTimeout(()=>setCopied(false),2000);
    });
  };
  return (
    <div className="sc__trackid-display-card">
      <div className="sc__trackid-display-eyebrow"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> Your Application Tracking ID</div>
      <div className="sc__trackid-display-row">
        <div className="sc__trackid-display-id">{trackId}</div>
        <button className={`sc__trackid-copy-btn${copied?" sc__trackid-copy-btn--copied":""}`} onClick={handleCopy} title="Copy Tracking ID">
          {copied ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>Copied!</> : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy ID</>}
        </button>
      </div>
      <div className="sc__trackid-display-hint">Save this ID to track your application status anytime using the tracking panel below.</div>
    </div>
  );
}

// ── FIX 3: PDF Receipt Generator — includes ALL user information ──────────────
function generateFullPDFReceipt({ trackId, applications, slot, dateStr, center, travelers, total, gst, method, purpose, sponsor, sponsorData, forms }) {
  const win = window.open("", "_blank");
  if (!win) return;

  const purposeLabel = TRAVEL_PURPOSES.find(p => p.id === purpose)?.label || "Tourism";
  const sponsorLabel = SPONSOR_OPTIONS.find(s => s.id === sponsor)?.label || "—";

  // Build per-traveller HTML blocks with ALL their info
  const travellersHTML = applications.map((app, i) => {
    const f = forms[i] || {};
    return `
      <div class="section">
        <div class="section-title">
          <span class="badge">${i + 1}</span>
          ${app.name || `Traveller ${i + 1}`}
          <span class="app-id">${app.appId}</span>
        </div>

        <div class="subsection-title">Personal Information</div>
        <table class="info-table">
          <tr><td class="key">Full Name</td><td class="val">${(f.firstName || "") + " " + (f.lastName || "") || "—"}</td>
              <td class="key">Gender</td><td class="val">${f.gender || "—"}</td></tr>
          <tr><td class="key">Date of Birth</td><td class="val">${f.dob ? new Date(f.dob).toLocaleDateString(LOCALE) : "—"}</td>
              <td class="key">Prior Schengen Visits</td><td class="val">${f.priorVisits || "0"}</td></tr>
        </table>

        <div class="subsection-title">Passport Details</div>
        <table class="info-table">
          <tr><td class="key">Passport Number</td><td class="val">${f.passportNo || "—"}</td>
              <td class="key">Place of Issue</td><td class="val">${f.placeOfIssue || "—"}</td></tr>
          <tr><td class="key">Valid Until</td><td class="val">${f.passportValidity ? new Date(f.passportValidity).toLocaleDateString(LOCALE) : "—"}</td>
              <td class="key">&nbsp;</td><td class="val">&nbsp;</td></tr>
        </table>

        <div class="subsection-title">Contact Details</div>
        <table class="info-table">
          <tr><td class="key">Email Address</td><td class="val">${f.email || "—"}</td>
              <td class="key">Phone Number</td><td class="val">${f.phone || "—"}</td></tr>
        </table>

        <div class="subsection-title">Emergency Contact</div>
        <table class="info-table">
          <tr><td class="key">Contact Name</td><td class="val">${f.emergName || "—"}</td>
              <td class="key">Relationship</td><td class="val">${f.emergRel || "—"}</td></tr>
          <tr><td class="key">Contact Phone</td><td class="val">${f.emergPhone || "—"}</td>
              <td class="key">&nbsp;</td><td class="val">&nbsp;</td></tr>
        </table>
      </div>
    `;
  }).join("");

  // Sponsor section
  const sponsorHTML = sponsor === "other" ? `
    <div class="section">
      <div class="section-title">Sponsorship Information</div>
      <table class="info-table">
        <tr><td class="key">Sponsor</td><td class="val">${sponsorData.name || "—"}</td>
            <td class="key">Relationship</td><td class="val">${sponsorData.rel || "—"}</td></tr>
        <tr><td class="key">Phone</td><td class="val">${sponsorData.phone || "—"}</td>
            <td class="key">Email</td><td class="val">${sponsorData.email || "—"}</td></tr>
        <tr><td class="key">ID Type</td><td class="val">${SPONSOR_ID_TYPES.find(t => t.id === sponsorData.idType)?.label || "—"}</td>
            <td class="key">ID Number</td><td class="val">${sponsorData.idNumber || "—"}</td></tr>
        <tr><td class="key">Sponsorship Letter</td><td class="val" colspan="3">${sponsorData.letterAction === "upload" ? "Uploaded by applicant" : sponsorData.letterAction === "service" ? "Requested via service" : "—"}</td></tr>
      </table>
    </div>
  ` : `
    <div class="section">
      <div class="section-title">Sponsorship</div>
      <table class="info-table">
        <tr><td class="key">Funding Type</td><td class="val" colspan="3">Self-Sponsored</td></tr>
      </table>
    </div>
  `;

  win.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8" />
      <title>Receipt — ${trackId}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 11px;
          color: #111;
          background: #fff;
          padding: 0;
        }

        /* ── Page Header ── */
        .page-header {
          background: #0f1f5c;
          color: white;
          padding: 28px 40px 20px;
        }
        .company-name {
          font-size: 22px;
          font-weight: 700;
          letter-spacing: 0.04em;
          margin-bottom: 4px;
        }
        .receipt-title {
          font-size: 13px;
          opacity: 0.75;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .header-meta {
          margin-top: 16px;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        .tracking-block { }
        .tracking-label {
          font-size: 8px;
          opacity: 0.6;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          margin-bottom: 4px;
        }
        .tracking-id {
          font-size: 16px;
          font-weight: 700;
          letter-spacing: 0.06em;
          font-family: 'Courier New', monospace;
        }
        .generated-on {
          font-size: 9px;
          opacity: 0.6;
          text-align: right;
        }

        /* ── Status Bar ── */
        .status-bar {
          background: #059669;
          color: white;
          padding: 10px 40px;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 8px;
          letter-spacing: 0.04em;
        }
        .status-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #86efac;
          flex-shrink: 0;
        }

        /* ── Summary Strip ── */
        .summary-strip {
          background: #eef2fb;
          border-bottom: 1px solid #dde1ee;
          padding: 14px 40px;
          display: flex;
          gap: 0;
        }
        .summary-item {
          flex: 1;
          padding-right: 20px;
          border-right: 1px solid #dde1ee;
          margin-right: 20px;
        }
        .summary-item:last-child { border-right: none; margin-right: 0; }
        .summary-label {
          font-size: 8px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #5b617c;
          margin-bottom: 3px;
        }
        .summary-value {
          font-size: 12px;
          font-weight: 700;
          color: #0f1f5c;
        }

        /* ── Body ── */
        .body {
          padding: 24px 40px 40px;
        }

        /* ── Section ── */
        .section {
          border: 1px solid #dde1ee;
          border-radius: 6px;
          margin-bottom: 16px;
          overflow: hidden;
          page-break-inside: avoid;
        }
        .section-title {
          background: #0f1f5c;
          color: white;
          padding: 9px 14px;
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 8px;
          letter-spacing: 0.02em;
        }
        .badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px; height: 20px;
          border-radius: 50%;
          background: rgba(255,255,255,0.25);
          font-size: 10px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .app-id {
          margin-left: auto;
          font-size: 9px;
          font-family: 'Courier New', monospace;
          opacity: 0.7;
          letter-spacing: 0.05em;
        }

        .subsection-title {
          background: #f8f9fb;
          border-top: 1px solid #dde1ee;
          padding: 6px 14px;
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #263e8a;
        }

        /* ── Info Tables ── */
        .info-table {
          width: 100%;
          border-collapse: collapse;
        }
        .info-table td {
          padding: 6px 14px;
          border-bottom: 1px solid #f3f4f6;
          font-size: 10.5px;
          vertical-align: top;
        }
        .info-table tr:last-child td { border-bottom: none; }
        .info-table .key {
          color: #5b617c;
          font-weight: 600;
          width: 18%;
          white-space: nowrap;
        }
        .info-table .val {
          color: #0f1f5c;
          font-weight: 700;
          width: 32%;
        }

        /* ── Fee Table ── */
        .fee-section {
          border: 2px solid #0f1f5c;
          border-radius: 6px;
          overflow: hidden;
          margin-bottom: 16px;
          page-break-inside: avoid;
        }
        .fee-header {
          background: #0f1f5c;
          color: white;
          padding: 9px 14px;
          font-size: 11px;
          font-weight: 700;
        }
        .fee-table {
          width: 100%;
          border-collapse: collapse;
        }
        .fee-table td {
          padding: 8px 14px;
          border-bottom: 1px solid #eef2fb;
          font-size: 11px;
        }
        .fee-table tr:last-child td { border-bottom: none; }
        .fee-table .fee-label { color: #5b617c; }
        .fee-table .fee-amt { text-align: right; font-weight: 700; color: #0f1f5c; font-family: 'Courier New', monospace; }
        .fee-total-row td {
          background: #eef2fb;
          border-top: 2px solid #0f1f5c !important;
          font-weight: 700;
          font-size: 13px;
          padding: 10px 14px;
        }
        .fee-total-row .fee-amt { color: #0f1f5c; font-size: 15px; }

        /* ── Disclaimer ── */
        .disclaimer {
          margin-top: 20px;
          padding: 12px 16px;
          background: #fef9e7;
          border: 1px solid #B8941F;
          border-radius: 6px;
          font-size: 9px;
          color: #7a5f0e;
          line-height: 1.6;
          page-break-inside: avoid;
        }
        .disclaimer strong { color: #5a430a; }

        /* ── Page footer ── */
        .page-footer {
          background: #f8f9fb;
          border-top: 1px solid #dde1ee;
          padding: 14px 40px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 9px;
          color: #9199b2;
        }

        @media print {
          button { display: none !important; }
          body { padding: 0; }
          .page-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .status-bar { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .summary-strip { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .section-title { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .fee-header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .fee-total-row td { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .subsection-title { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .disclaimer { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style>
    </head>
    <body>

      <!-- PAGE HEADER -->
      <div class="page-header">
        <div class="company-name">TheVisaCo</div>
        <div class="receipt-title">Official Booking Confirmation &amp; Receipt</div>
        <div class="header-meta">
          <div class="tracking-block">
            <div class="tracking-label">Tracking ID</div>
            <div class="tracking-id">${trackId}</div>
          </div>
          <div class="generated-on">
            Generated on<br/>
            ${new Date().toLocaleDateString(LOCALE, { day:"2-digit", month:"long", year:"numeric" })}<br/>
            ${new Date().toLocaleTimeString(LOCALE)}
          </div>
        </div>
      </div>

      <!-- STATUS BAR -->
      <div class="status-bar">
        <div class="status-dot"></div>
        PAYMENT CONFIRMED — Booking Successful
      </div>

      <!-- SUMMARY STRIP -->
      <div class="summary-strip">
        <div class="summary-item">
          <div class="summary-label">Destination</div>
          <div class="summary-value">${slot?.destination || "—"}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Appointment Date</div>
          <div class="summary-value">${dateStr}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Application Centre</div>
          <div class="summary-value">${center?.name || "—"}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Travel Purpose</div>
          <div class="summary-value">${purposeLabel}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Travellers</div>
          <div class="summary-value">${travelers}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Amount Paid</div>
          <div class="summary-value">${formatINR(total)}</div>
        </div>
      </div>

      <!-- BODY -->
      <div class="body">

        <!-- TRAVELLER SECTIONS -->
        ${travellersHTML}

        <!-- SPONSOR SECTION -->
        ${sponsorHTML}

        <!-- APPOINTMENT SECTION -->
        <div class="section">
          <div class="section-title">Appointment Details</div>
          <table class="info-table">
            <tr><td class="key">Destination Country</td><td class="val">${slot?.destination || "—"}</td>
                <td class="key">Application Centre</td><td class="val">${center?.name || "—"}</td></tr>
            <tr><td class="key">Centre Address</td><td class="val">${center?.address || "—"}</td>
                <td class="key">Appointment Date</td><td class="val">${dateStr}</td></tr>
            <tr><td class="key">Travel Purpose</td><td class="val">${purposeLabel}</td>
                <td class="key">Sponsorship Type</td><td class="val">${sponsorLabel}</td></tr>
          </table>
        </div>

        <!-- FEE SECTION -->
        <div class="fee-section">
          <div class="fee-header">Payment Summary</div>
          <table class="fee-table">
            <tr>
              <td class="fee-label">Appointment Fee (${formatINR(FEE_BASE.appointment)} × ${travelers} traveller${travelers>1?"s":""})</td>
              <td class="fee-amt">${formatINR(FEE_BASE.appointment * travelers)}</td>
            </tr>
            <tr>
              <td class="fee-label">Service Fee (${formatINR(FEE_BASE.service)} × ${travelers} traveller${travelers>1?"s":""})</td>
              <td class="fee-amt">${formatINR(FEE_BASE.service * travelers)}</td>
            </tr>
            <tr>
              <td class="fee-label">GST @ 18%</td>
              <td class="fee-amt">${formatINR(gst)}</td>
            </tr>
            ${method.convenience > 0 ? `<tr><td class="fee-label">Convenience Fee (${method.label})</td><td class="fee-amt">${formatINR(method.convenience)}</td></tr>` : ""}
            <tr class="fee-total-row">
              <td class="fee-label">TOTAL AMOUNT PAID</td>
              <td class="fee-amt">${formatINR(total)}</td>
            </tr>
          </table>
        </div>

        <!-- DISCLAIMER -->
        <div class="disclaimer">
          <strong>Important Notice:</strong> TheVisaCo is an independent visa appointment assistance service and is not affiliated with, endorsed by, or connected to any embassy, consulate, or government authority. Payment of service fees does not guarantee visa approval or appointment confirmation. All fees are strictly non-refundable. This is a computer-generated receipt — no signature is required. For queries, contact support@thevisaco.com.
        </div>
      </div>

      <!-- PAGE FOOTER -->
      <div class="page-footer">
        <div>TheVisaCo.com · Schengen Visa Appointment Service · India</div>
        <div>Tracking ID: ${trackId} · Generated ${new Date().toLocaleDateString(LOCALE)}</div>
      </div>

      <script>window.onload = function(){ window.print(); }<\/script>
    </body>
    </html>
  `);
  win.document.close();
}

// ── Success Screen ────────────────────────────────────────────
function SuccessScreen({ slot, selectedDate, selectedCenter, forms, travelers, payMethod, purpose, sponsor, sponsorData, onReset }) {
  useAOS();
  const center = APP_CENTERS.find(c=>c.id===selectedCenter);
  const dateStr = selectedDate ? `${selectedDate.day} ${MONTH_NAMES[selectedDate.month]} ${selectedDate.year}` : "—";
  const method  = PAYMENT_METHODS.find(m=>m.id===payMethod)||PAYMENT_METHODS[0];
  const [trackId]    = useState(()=>generateTrackId());
  const [appIds]     = useState(()=>Array.from({length:travelers},(_,i)=>generateAppId(i)));
  const [showDetail, setShowDetail] = useState(null);

  const sub   = (FEE_BASE.appointment + FEE_BASE.service) * travelers;
  const gst   = Math.round(sub * FEE_BASE.gstRate);
  const total = sub + gst + method.convenience;

  const applications = Array.from({length:travelers},(_,i)=>{
    const f = forms[i]||{};
    return {
      appId:      appIds[i],
      name:       `${f.firstName||""} ${f.lastName||""}`.trim()||`Traveller ${i+1}`,
      passportNo: f.passportNo||"—",
      email:      f.email||"—",
      phone:      f.phone||"—",
      gender:     f.gender||"—",
      dob:        f.dob ? new Date(f.dob).toLocaleDateString(LOCALE) : "—",
      emergName:  f.emergName||"—",
      emergRel:   f.emergRel||"—",
      emergPhone: f.emergPhone||"—",
      destination:`${slot?.flag} ${slot?.destination}`,
      date:       dateStr,
      center:     center?.name||"—",
    };
  });

  useEffect(()=>{
    const record = { trackId, destination:`${slot?.flag} ${slot?.destination}`, appointmentDate:dateStr, center:center?.name||"—", totalPaid:total, currentStage:"paid", updatedAt:Date.now(), applications };
    saveTrackRecord(record);
  }, []); // eslint-disable-line

  return (
    <div className="sc__success">
      <div className="sc__success-title" data-aos="fade-up">🎉 Booking Confirmed!</div>
      <p className="sc__success-sub" data-aos="fade-up" data-aos-delay="50">
        Your Schengen visa appointment{travelers>1?"s":""} {travelers>1?"have":"has"} been booked successfully. Check your email for confirmation &amp; documents.
      </p>
      <div data-aos="fade-up" data-aos-delay="100"><TrackingIdCard trackId={trackId} /></div>
      <div style={{marginBottom:"2rem"}} data-aos="fade-up" data-aos-delay="150"><TrackingPanel /></div>

      <div style={{marginBottom:"1.25rem"}} data-aos="fade-up" data-aos-delay="200">
        <div style={{fontFamily:"var(--ff3)",fontSize:"0.75rem",fontWeight:700,color:"var(--navy)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.75rem",textAlign:"left"}}>
          Application{travelers>1?"s":""} ({travelers})
        </div>
        {applications.map((app,i)=>(
          <div key={i} className="sc__success-card" style={{cursor:"pointer"}} onClick={()=>setShowDetail(app)}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.75rem"}}>
              <div style={{fontFamily:"var(--ff3)",fontSize:"0.75rem",fontWeight:700,color:"var(--navy)",textTransform:"uppercase",letterSpacing:"0.08em"}}>Application {i+1}</div>
              <span style={{fontSize:"0.72rem",color:"var(--navy-light)",fontFamily:"var(--ff3)",fontWeight:600}}>Tap for details </span>
            </div>
            <div className="sc__success-row"><span>Application ID</span><span style={{fontFamily:"monospace",fontSize:"0.8rem"}}>{app.appId}</span></div>
            <div className="sc__success-row"><span>Traveller</span><span>{app.name}</span></div>
            <div className="sc__success-row"><span>Destination</span><span>{app.destination}</span></div>
            <div className="sc__success-row"><span>Appointment</span><span>{app.date}</span></div>
            <div className="sc__success-row"><span>Passport No.</span><span>{app.passportNo}</span></div>
          </div>
        ))}
      </div>

      <div className="sc__success-card" data-aos="fade-up" data-aos-delay="250">
        <div style={{fontFamily:"var(--ff3)",fontSize:"0.75rem",fontWeight:700,color:"var(--navy)",marginBottom:"0.75rem",textTransform:"uppercase",letterSpacing:"0.08em"}}>Payment Summary</div>
        <div className="sc__success-row"><span>Appointment Fee × {travelers}</span><span>{formatINR(FEE_BASE.appointment * travelers)}</span></div>
        <div className="sc__success-row"><span>Service Fee × {travelers}</span><span>{formatINR(FEE_BASE.service * travelers)}</span></div>
        <div className="sc__success-row"><span>GST (18%)</span><span>{formatINR(gst)}</span></div>
        {method.convenience>0 && <div className="sc__success-row"><span>Convenience Fee</span><span>{formatINR(method.convenience)}</span></div>}
        <div className="sc__success-row" style={{borderTop:"2px solid var(--navy)",marginTop:"0.5rem",paddingTop:"0.5rem"}}>
          <span style={{fontFamily:"var(--ff3)",fontWeight:700,color:"var(--navy)"}}>Total Paid</span>
          <span style={{fontFamily:"var(--ff)",fontSize:"1.1rem"}}>{formatINR(total)}</span>
        </div>
      </div>

      <div className="sc__success-actions" data-aos="fade-up" data-aos-delay="300">
        {/* FIX 3: Full PDF with all user data */}
        <button className="sc__btn sc__btn--navy" onClick={()=>generateFullPDFReceipt({ trackId, applications, slot, dateStr, center, travelers, total, gst, method, purpose, sponsor, sponsorData, forms })}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Download Receipt (PDF)
        </button>
        <button className="sc__btn sc__btn--outline" onClick={onReset}>← Start New Application</button>
      </div>

      {showDetail && <AppDetailModal app={showDetail} onClose={()=>setShowDetail(null)} />}
    </div>
  );
}

// ── Root Component ────────────────────────────────────────────
export default function SchengenChecker() {
  const [step,                  setStep]                  = useState(1);
  const [selectedSlot,          setSelectedSlot]          = useState(null);
  const [selectedDate,          setSelectedDate]          = useState(null);
  const [selectedCenter,        setSelectedCenter]        = useState("c1");
  const [travelers,             setTravelers]             = useState(1);
  const [purpose,               setPurpose]               = useState(null);
  const [forms,                 setForms]                 = useState([{}]);
  const [sponsor,               setSponsor]               = useState(null);
  const [sponsorData,           setSponsorData]           = useState({});
  const [sponsorLetterUploaded, setSponsorLetterUploaded] = useState(false);
  const [agreed,                setAgreed]                = useState(false);
  const [consentChecked,        setConsentChecked]        = useState(false);
  const [payMethod,             setPayMethod]             = useState("debit");
  const [paymentDetails,        setPaymentDetails]        = useState(PAYMENT_DETAILS_DEFAULT);
  const setPayF = (e) => setPaymentDetails(d => ({...d, [e.target.name]: e.target.value}));
  const [done,                  setDone]                  = useState(false);

  useEffect(()=>{
    setForms(prev=>{
      const next = Array.from({length:travelers},(_,i)=>({...(prev[i]||{}), _appId: prev[i]?._appId||generateAppId(i)}));
      return next;
    });
  }, [travelers]);

  const canNext = useCallback(()=>{
    if (step===1) return false;
    if (step===2) return !!selectedDate;
    if (step===3) return !!purpose;
    if (step===4) {
      return forms.slice(0,travelers).every(f=>f.firstName&&f.lastName&&f.passportNo&&f.email&&f.phone&&f.emergName&&f.emergPhone&&f.gender);
    }
    if (step===5) {
      if (!sponsor) return false;
      if (sponsor==="other") {
        const sd=sponsorData;
        if (!sd.name||!sd.phone||!sd.email||!sd.idType||!sd.idNumber) return false;
        if (!sd.letterAction) return false;
        if (sd.letterAction==="upload"&&!sd.letterFile) return false;
        if (sd.idType==="passport"&&(!sd.idFront||!sd.idBack)) return false;
        if (sd.idType!=="passport"&&!sd.idPhoto) return false;
      }
      return true;
    }
    if (step===6) return true;
    if (step===7) return consentChecked;
    return false;
  }, [step, selectedDate, purpose, forms, travelers, sponsor, sponsorData, consentChecked]);

  const goNext = ()=>{ if(step<8) setStep(s=>s+1); };
  const goPrev = ()=>{ if(step>1) setStep(s=>s-1); };
  const handleApply = (slot)=>{ setSelectedSlot(slot); setStep(2); };
  const handleReset = ()=>{
    setStep(1); setSelectedSlot(null); setSelectedDate(null); setSelectedCenter("c1");
    setTravelers(1); setPurpose(null); setForms([{}]); setSponsor(null); setSponsorData({});
    setSponsorLetterUploaded(false); setAgreed(false); setConsentChecked(false);
    setPayMethod("debit"); setPaymentDetails(PAYMENT_DETAILS_DEFAULT); setDone(false);
  };

  return (
    <div className="sc">
      <div className="sc__header" data-aos="fade-up">
        <div className="sc__header-title">Schengen Visa Checker</div>
        <div className="sc__header-sub">Check availability, book slots &amp; file your application — all in one place</div>
      </div>

      {!done && (
        <div className="sc__steps" data-aos="fade-up" data-aos-delay="100">
          <div className="sc__steps-inner">
            {STEPS.map(s=>(
              <div key={s.id} className={`sc__step${step===s.id?" sc__step--active":""}${step>s.id?" sc__step--done":""}`}>
                <div className="sc__step-circle">
                  {step>s.id ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg> : s.id}
                </div>
                <div className="sc__step-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="sc__body">
        {done ? (
          <SuccessScreen
            slot={selectedSlot}
            selectedDate={selectedDate}
            selectedCenter={selectedCenter}
            forms={forms}
            travelers={travelers}
            payMethod={payMethod}
            purpose={purpose}
            sponsor={sponsor}
            sponsorData={sponsorData}
            onReset={handleReset}
          />
        ) : (
          <>
            {step===1 && <Step1Search onApply={handleApply} />}
            {step===2 && <Step2Appointment slot={selectedSlot} selectedDate={selectedDate} onDateSelect={setSelectedDate} selectedCenter={selectedCenter} onCenterSelect={setSelectedCenter} />}
            {step===3 && <Step3TravelInfo travelers={travelers} setTravelers={setTravelers} purpose={purpose} setPurpose={setPurpose} />}
            {step===4 && <Step4TravellerDetails forms={forms} setForms={setForms} travelers={travelers} />}
            {step===5 && <Step5Sponsor sponsor={sponsor} setSponsor={setSponsor} sponsorData={sponsorData} setSponsorData={setSponsorData} sponsorLetterUploaded={sponsorLetterUploaded} setSponsorLetterUploaded={setSponsorLetterUploaded} />}
            {step===6 && <Step6Documents form={forms[0]||{}} slot={selectedSlot} purpose={purpose} sponsorLetterUploaded={sponsorLetterUploaded} />}
            {step===7 && <Step7Agreement agreed={agreed} setAgreed={setAgreed} consentChecked={consentChecked} setConsentChecked={setConsentChecked} />}
            {step===8 && <Step8Payment payMethod={payMethod} setPayMethod={setPayMethod} paymentDetails={paymentDetails} setPayF={setPayF} setPaymentDetails={setPaymentDetails} travelers={travelers} onPay={()=>setDone(true)} />}

            {step>1 && (
              <div className="sc__step-nav">
                <button className="sc__btn sc__btn--ghost" onClick={goPrev}>Back</button>
                <div style={{display:"flex",alignItems:"center",gap:"0.5rem"}}>
                  {step<8 && (
                    <button className={`sc__btn sc__btn--navy${!canNext()?" sc__btn--disabled":""}`} onClick={canNext()?goNext:undefined} disabled={!canNext()}>Continue</button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
